import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Dumbbell, Heart, Activity, Brain, Timer, Target, 
  ChevronRight, X, Search, Filter, Moon, Flame,
  Zap, Waves, Wind, Scroll, Bike
} from 'lucide-react';
import { WorkoutPlayer2 } from '../workouts/player/WorkoutPlayer2';

interface ActivitySelectorProps {
  onClose: () => void;
}

interface Category {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
}

const categories: Category[] = [
  { id: 'strength', name: 'Strength', icon: <Dumbbell className="w-5 h-5" />, color: 'from-blue-500/20 to-indigo-500/20' },
  { id: 'cardio', name: 'Cardio', icon: <Heart className="w-5 h-5" />, color: 'from-red-500/20 to-pink-500/20' },
  { id: 'hiit', name: 'HIIT', icon: <Activity className="w-5 h-5" />, color: 'from-orange-500/20 to-amber-500/20' },
  { id: 'recovery', name: 'Recovery', icon: <Brain className="w-5 h-5" />, color: 'from-green-500/20 to-emerald-500/20' },
  { id: 'sleep', name: 'Sleep', icon: <Moon className="w-5 h-5" />, color: 'from-purple-500/20 to-violet-500/20' },
  { id: 'mobility', name: 'Mobility', icon: <Scroll className="w-5 h-5" />, color: 'from-teal-500/20 to-cyan-500/20' },
  { id: 'cycling', name: 'Cycling', icon: <Bike className="w-5 h-5" />, color: 'from-indigo-500/20 to-violet-500/20' }
];

const activities = {
  strength: [
    { id: 'weight-training', name: 'Weight Training', duration: 60, strain: 14.5 },
    { id: 'bodyweight', name: 'Bodyweight Training', duration: 45, strain: 12.0 },
    { id: 'powerlifting', name: 'Powerlifting', duration: 75, strain: 16.0 }
  ],
  cardio: [
    { id: 'steady-state', name: 'Steady State Cardio', duration: 45, strain: 10.5 },
    { id: 'intervals', name: 'Interval Training', duration: 30, strain: 13.0 },
    { id: 'endurance', name: 'Endurance Training', duration: 90, strain: 15.0 }
  ],
  hiit: [
    { id: 'tabata', name: 'Tabata', duration: 20, strain: 15.5 },
    { id: 'amrap', name: 'AMRAP', duration: 30, strain: 16.0 },
    { id: 'emom', name: 'EMOM', duration: 25, strain: 14.5 }
  ],
  recovery: [
    { id: 'stretching', name: 'Dynamic Stretching', duration: 20, strain: 4.0 },
    { id: 'foam-rolling', name: 'Foam Rolling', duration: 15, strain: 3.0 },
    { id: 'mobility-work', name: 'Mobility Work', duration: 30, strain: 5.0 }
  ],
  sleep: [
    { id: 'sleep-meditation', name: 'Sleep Meditation', duration: 15, strain: 1.0 },
    { id: 'breathwork', name: 'Sleep Breathwork', duration: 10, strain: 1.0 },
    { id: 'relaxation', name: 'Progressive Relaxation', duration: 20, strain: 1.0 }
  ],
  mobility: [
    { id: 'yoga-flow', name: 'Yoga Flow', duration: 45, strain: 7.0 },
    { id: 'joint-mobility', name: 'Joint Mobility', duration: 20, strain: 4.0 },
    { id: 'flexibility', name: 'Flexibility Work', duration: 30, strain: 5.0 }
  ],
  cycling: [
    { id: 'spin-class', name: 'Spin Class', duration: 45, strain: 13.0 },
    { id: 'endurance-ride', name: 'Endurance Ride', duration: 60, strain: 11.0 },
    { id: 'interval-ride', name: 'Interval Ride', duration: 30, strain: 14.0 }
  ]
};

export function ActivitySelector({ onClose }: ActivitySelectorProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedActivity, setSelectedActivity] = useState<any | null>(null);

  const handleActivitySelect = (activity: any) => {
    setSelectedActivity({
      id: activity.id,
      name: activity.name,
      exercises: [
        {
          name: 'Warm Up',
          duration: 300,
          completed: false,
          intensity: 'Light',
          description: 'Gentle warm-up to prepare for activity'
        },
        {
          name: activity.name,
          duration: activity.duration * 60,
          completed: false,
          intensity: activity.strain > 14 ? 'High' : activity.strain > 8 ? 'Moderate' : 'Light',
          description: `Main ${activity.name} session`
        },
        {
          name: 'Cool Down',
          duration: 300,
          completed: false,
          intensity: 'Light',
          description: 'Gentle cool-down and stretching'
        }
      ],
      targetStrain: activity.strain
    });
  };

  if (selectedActivity) {
    return (
      <WorkoutPlayer2
        workout={selectedActivity}
        onClose={() => {
          setSelectedActivity(null);
          onClose();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Start Activity</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search activities..."
              className="w-full bg-muted/50 border border-border/50 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>

          {/* Categories */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-4 rounded-lg relative overflow-hidden group ${
                  selectedCategory === category.id ? 'ring-2 ring-primary' : ''
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-50`} />
                <div className="relative flex flex-col items-center gap-2">
                  {category.icon}
                  <span className="font-medium">{category.name}</span>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Activities List */}
          {selectedCategory && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {activities[selectedCategory as keyof typeof activities]
                .filter(activity => 
                  activity.name.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((activity) => (
                  <motion.button
                    key={activity.id}
                    onClick={() => handleActivitySelect(activity)}
                    className="theme-card p-4 relative overflow-hidden group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
                    <div className="relative flex items-center justify-between">
                      <div>
                        <div className="font-medium">{activity.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {activity.duration} min • Strain {activity.strain}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </motion.button>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}